import tcLib from '@cclib/lib/timecode.js';
import convertToPlainText from '@cclib/functions/quill/convertToPlainText.js';

/**
 * @typedef {Object} TranscriptLabelSet
 * @property {string} yes
 * @property {string} no
 * @property {string} approved
 * @property {string} failed
 * @property {string} needsReview
 * @property {string} notes
 * @property {string} notesCheck
 * @property {string} reply
 * @property {string} replyCheck
 * @property {string} approvedLabel
 * @property {string} forced
 * @property {string} tags
 * @property {string} original
 * @property {string} metadataHeader
 * @property {string} metadataNone
 */

/** @returns {TranscriptLabelSet} */
export function getDefaultLabels() {
    return {
        yes: 'Yes',
        no: 'No',
        approved: 'Approved',
        failed: 'Failed',
        needsReview: 'Needs Review',
        notes: 'Notes',
        notesCheck: 'Notes Checked',
        reply: 'Reply',
        replyCheck: 'Reply Checked',
        approvedLabel: 'Approved',
        forced: 'Forced',
        tags: 'Tags',
        original: 'Original',
        metadataHeader: '****METADATA****',
        metadataNone: 'No metadata found',
    };
}

/**
 * @param {number} seconds
 * @param {'smpte' | 'ms' | 'seconds'} tcFormat
 * @param {{ frameRate: number|string, dropFrame: boolean }} project
 * @returns {string}
 */
export function formatTimecode(seconds, tcFormat, project) {
    switch (tcFormat) {
        case 'smpte':
            return tcLib.secToTc(seconds, project.frameRate, project.dropFrame);
        case 'ms':
            return tcLib.secToTcMs(seconds);
        default:
            return String(seconds);
    }
}

/**
 * @param {Array<{ name?: string } | string>} speakers
 * @param {string} [separator=' | ']
 * @returns {string}
 */
export function formatSpeakers(speakers = [], separator = ' | ') {
    return speakers
        .map((speaker) => (typeof speaker === 'string' ? speaker : speaker?.name || ''))
        .filter(Boolean)
        .join(separator);
}

/**
 * @param {Array<{ term?: string } | string>} tags
 * @param {string} [separator=', ']
 * @returns {string}
 */
export function formatTags(tags = [], separator = ', ') {
    return tags
        .map((tag) => (typeof tag === 'string' ? tag : tag?.term || ''))
        .filter(Boolean)
        .join(separator);
}

/**
 * @param {boolean | undefined} value
 * @param {TranscriptLabelSet} labels
 * @returns {string}
 */
export function formatYesNo(value, labels) {
    return value ? labels.yes : labels.no;
}

/**
 * @param {boolean | undefined} approved
 * @param {TranscriptLabelSet} labels
 * @returns {string}
 */
export function formatApproved(approved, labels) {
    if (approved === true) return labels.approved;
    if (approved === false) return labels.failed;
    return labels.needsReview;
}

/**
 * @param {string | undefined} text
 * @param {string} [lineBreak=' ']
 * @returns {string}
 */
export function formatPlainText(text, lineBreak = ' ') {
    const plain = convertToPlainText(text || '', lineBreak);
    return (lineBreak.includes('\n') ? trimLineEdges(plain) : plain).trim();
}

/**
 * Remove spaces/tabs on either side of each line break.
 * @param {string} text
 * @returns {string}
 */
function trimLineEdges(text) {
    return text.replace(/[ \t\u00a0]*\n[ \t\u00a0]*/g, '\n');
}

/**
 * @param {string} value
 * @param {'none' | 'square' | 'round' | 'curly'} [wrap='none']
 * @returns {string}
 */
export function wrapValue(value, wrap = 'none') {
    switch (wrap) {
        case 'square': return `[${value}]`;
        case 'round': return `(${value})`;
        case 'curly': return `{${value}}`;
        default: return value;
    }
}

/**
 * Normalize line breaks in a raw (non-rich) string.
 * @param {string | undefined} text
 * @param {boolean} preserve Keep breaks as "\n"; otherwise collapse them into single spaces.
 * @returns {string}
 */
export function normalizeLineBreaks(text, preserve) {
    const value = String(text ?? '');
    if (preserve) return trimLineEdges(value.replace(/\r\n?/g, '\n')).trim();
    return value.replace(/[ \t]*(\r\n?|\n)+[ \t]*/g, ' ').trim();
}

/**
 * Build optional metadata lines for a single event (cued / CSV display).
 * @param {object} event
 * @param {object} options
 * @param {TranscriptLabelSet} labels
 * @returns {Array<{ label: string, value: string }>}
 */
export function getEventMetaEntries(event, options, labels) {
    /** @type {Array<{ label: string, value: string }>} */
    const entries = [];

    if (options.exportTags) {
        const tags = formatTags(event.tags || []);
        if (tags) entries.push({ label: labels.tags, value: tags });
    }
    if (options.exportNotes && event.notes) {
        entries.push({ label: labels.notes, value: String(event.notes) });
    }
    if (options.exportNotesCheck) {
        entries.push({ label: labels.notesCheck, value: formatYesNo(Boolean(event.notesCheck), labels) });
    }
    if (options.exportReply && event.reply) {
        entries.push({ label: labels.reply, value: String(event.reply) });
    }
    if (options.exportReplyCheck) {
        entries.push({ label: labels.replyCheck, value: formatYesNo(Boolean(event.replyCheck), labels) });
    }
    if (options.exportApproved) {
        entries.push({ label: labels.approvedLabel, value: formatApproved(event.approved, labels) });
    }
    if (options.exportForced) {
        entries.push({ label: labels.forced, value: formatYesNo(Boolean(event.forced), labels) });
    }

    return entries;
}

/**
 * @param {object} event
 * @param {boolean} includeSpeakers
 * @returns {string}
 */
export function formatSpokenLine(event, includeSpeakers) {
    const text = formatPlainText(event.text);
    if (!includeSpeakers) return text;
    const speakers = formatSpeakers(event.speakers || [], ', ');
    if (!speakers) return text;
    return `${speakers}: ${text}`;
}

/**
 * @param {boolean} exportIncode
 * @param {boolean} exportOutcode
 * @param {number} start
 * @param {number} end
 * @param {'smpte' | 'ms' | 'seconds'} tcFormat
 * @param {{ frameRate: number|string, dropFrame: boolean }} project
 * @returns {string}
 */
export function formatTimecodeLine(exportIncode, exportOutcode, start, end, tcFormat, project) {
    const parts = [];
    if (exportIncode) parts.push(formatTimecode(start, tcFormat, project));
    if (exportOutcode) parts.push(formatTimecode(end, tcFormat, project));
    return parts.join('\t');
}
